// Permission-Groups (Rechte-Pakete) Verwaltungs-Tab.
// Admin-only. Erlaubt das Anlegen/Bearbeiten/Loeschen von Gruppen
// und das Setzen der freigeschalteten Module pro Gruppe.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Plus, Trash2, Users, Boxes, Pencil, Save, X } from 'lucide-react'
import { apiClient } from '@/lib/api.ts'
import { getModulesByCategory } from '@shared/types'
import {
  Badge,
  Button,
  ConfirmDialog,
  EmptyState,
  Input,
  LoadingState,
  Modal,
  Switch,
  Textarea,
} from '@/components/ui'

interface GroupListEntry {
  id: string
  name: string
  description: string | null
  moduleCount: number
  memberCount: number
}

interface GroupDetail {
  id: string
  name: string
  description: string | null
  modules: Record<string, boolean>
  members: Array<{
    userId: string
    name: string
    email: string
    role: string
    isActive: boolean
  }>
}

function extractErrorMessage(err: unknown): string {
  const axErr = err as { response?: { data?: unknown }; message?: string }
  const data = axErr?.response?.data
  if (typeof data === 'string') return data
  if (data && typeof data === 'object') {
    const obj = data as Record<string, unknown>
    if (typeof obj.message === 'string') return obj.message
    if (typeof obj.error === 'string') return obj.error
  }
  if (typeof axErr?.message === 'string') return axErr.message
  return ''
}

export function PermissionGroupsTab() {
  const [groups, setGroups] = useState<GroupListEntry[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [showCreate, setShowCreate] = useState(false)
  const [pendingDelete, setPendingDelete] = useState<GroupListEntry | null>(null)
  // Initial-Mount-Flag, damit ein Re-Fetch nach Toggles NICHT zum
  // <LoadingState />-Flicker fuehrt — sonst wird die gesamte Detail-Ansicht
  // unmountet und der Browser scrollt zurueck nach oben.
  const initialLoadDone = useRef(false)

  const reload = useCallback(async () => {
    if (!initialLoadDone.current) setLoading(true)
    setError('')
    try {
      const { data } = await apiClient.get<GroupListEntry[]>('/permission-groups')
      setGroups(data)
    } catch (err) {
      setError(extractErrorMessage(err) || 'Gruppen konnten nicht geladen werden.')
    } finally {
      setLoading(false)
      initialLoadDone.current = true
    }
  }, [])

  useEffect(() => {
    reload()
  }, [reload])

  const handleCreate = async (name: string, description: string) => {
    await apiClient.post('/permission-groups', { name, description: description || null })
    setShowCreate(false)
    await reload()
  }

  const handleDelete = async (id: string) => {
    await apiClient.delete(`/permission-groups/${id}`)
    if (selectedId === id) setSelectedId(null)
    setPendingDelete(null)
    await reload()
  }

  if (loading) return <LoadingState />

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h2 className="text-h3 text-gray-900">Rechte-Gruppen</h2>
          <p className="text-caption text-gray-500 mt-0.5">
            Bündel von Modul-Berechtigungen, die Benutzern als Gruppe zugewiesen werden können.
            Persönliche Allow/Deny-Einstellungen pro Benutzer haben Vorrang (Deny gewinnt).
          </p>
        </div>
        <Button leadingIcon={<Plus className="w-4 h-4" />} onClick={() => setShowCreate(true)}>
          Neue Gruppe
        </Button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-800 text-small rounded px-3 py-2">
          {error}
        </div>
      )}

      {groups.length === 0 ? (
        <EmptyState
          title="Noch keine Rechte-Gruppen angelegt"
          description='Lege z.B. eine Gruppe „Techniker" an und weise ihr die zulässigen Module zu.'
          icon={<Users />}
          action={
            <Button leadingIcon={<Plus className="w-4 h-4" />} onClick={() => setShowCreate(true)}>
              Erste Gruppe anlegen
            </Button>
          }
        />
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="space-y-2">
            {groups.map((g) => (
              <button
                key={g.id}
                type="button"
                onClick={() => setSelectedId(g.id)}
                className={
                  'w-full text-left border rounded-md px-3 py-2 transition-colors ' +
                  (selectedId === g.id
                    ? 'border-brand-300 bg-brand-50'
                    : 'border-gray-200 bg-white hover:bg-gray-50')
                }
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <p className="text-small font-medium text-gray-900 truncate">{g.name}</p>
                    {g.description && (
                      <p className="text-caption text-gray-500 truncate mt-0.5">{g.description}</p>
                    )}
                  </div>
                  <button
                    type="button"
                    aria-label="Gruppe löschen"
                    onClick={(e) => {
                      e.stopPropagation()
                      setPendingDelete(g)
                    }}
                    className="p-1 rounded text-gray-400 hover:text-red-600 hover:bg-red-50"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
                <div className="flex items-center gap-2 mt-1.5">
                  <Badge variant="brand" size="sm">
                    <Boxes className="w-3 h-3 mr-0.5" />
                    {g.moduleCount} Module
                  </Badge>
                  <Badge variant="neutral" size="sm">
                    <Users className="w-3 h-3 mr-0.5" />
                    {g.memberCount} Mitglieder
                  </Badge>
                </div>
              </button>
            ))}
          </div>

          <div className="lg:col-span-2">
            {selectedId ? (
              <GroupDetailEditor
                key={selectedId}
                groupId={selectedId}
                onChanged={reload}
              />
            ) : (
              <div className="border border-dashed border-gray-200 rounded-md p-8 text-center text-caption text-gray-500">
                Wähle links eine Gruppe aus, um Module und Mitglieder zu bearbeiten.
              </div>
            )}
          </div>
        </div>
      )}

      {showCreate && (
        <CreateGroupModal onClose={() => setShowCreate(false)} onCreate={handleCreate} />
      )}

      {pendingDelete && (
        <ConfirmDialog
          open
          title={`Gruppe „${pendingDelete.name}" löschen?`}
          message={
            pendingDelete.memberCount > 0
              ? `Diese Gruppe hat ${pendingDelete.memberCount} Mitglieder. Die Mitgliedschaften werden ebenfalls entfernt; persönliche Modul-Overrides der Benutzer bleiben unverändert.`
              : 'Die Gruppe und alle Modul-Zuordnungen werden gelöscht.'
          }
          confirmLabel="Löschen"
          variant="danger"
          onConfirm={() => handleDelete(pendingDelete.id)}
          onCancel={() => setPendingDelete(null)}
        />
      )}
    </div>
  )
}

function CreateGroupModal({
  onClose,
  onCreate,
}: {
  onClose: () => void
  onCreate: (name: string, description: string) => Promise<void>
}) {
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  const handleSave = async () => {
    if (!name.trim()) {
      setError('Name erforderlich')
      return
    }
    setSaving(true)
    setError('')
    try {
      await onCreate(name.trim(), description.trim())
    } catch (err) {
      setError(extractErrorMessage(err) || 'Speichern fehlgeschlagen.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <Modal
      open
      onOpenChange={(next) => {
        if (!next) onClose()
      }}
      title="Neue Rechte-Gruppe"
    >
      <div className="space-y-3">
        <Input
          label="Name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="z.B. Techniker"
          autoFocus
        />
        <Textarea
          label="Beschreibung (optional)"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          rows={3}
          placeholder="Wofür ist diese Gruppe zuständig?"
        />
        {error && (
          <div className="text-caption text-red-700 bg-red-50 border border-red-200 rounded px-2 py-1">
            {error}
          </div>
        )}
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="secondary" onClick={onClose} disabled={saving}>
            Abbrechen
          </Button>
          <Button onClick={handleSave} loading={saving} leadingIcon={<Save className="w-4 h-4" />}>
            Anlegen
          </Button>
        </div>
      </div>
    </Modal>
  )
}

function GroupDetailEditor({
  groupId,
  onChanged,
}: {
  groupId: string
  onChanged: () => void
}) {
  const [detail, setDetail] = useState<GroupDetail | null>(null)
  const [loading, setLoading] = useState(true)
  const [savingModule, setSavingModule] = useState<string | null>(null)
  const [bulkSaving, setBulkSaving] = useState(false)
  const [error, setError] = useState('')
  const [editingMeta, setEditingMeta] = useState(false)
  const [editName, setEditName] = useState('')
  const [editDesc, setEditDesc] = useState('')
  const [savingMeta, setSavingMeta] = useState(false)

  const load = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      const { data } = await apiClient.get<GroupDetail>(`/permission-groups/${groupId}`)
      setDetail(data)
      setEditName(data.name)
      setEditDesc(data.description ?? '')
    } catch (err) {
      setError(extractErrorMessage(err) || 'Gruppe konnte nicht geladen werden.')
    } finally {
      setLoading(false)
    }
  }, [groupId])

  useEffect(() => {
    load()
  }, [load])

  const grouped = useMemo(() => getModulesByCategory(), [])

  const toggleModule = async (moduleId: string, allowed: boolean) => {
    if (!detail) return
    setSavingModule(moduleId)
    setError('')
    try {
      const { data } = await apiClient.put<{ modules: Record<string, boolean> }>(
        `/permission-groups/${groupId}/modules`,
        { modules: { [moduleId]: allowed } },
      )
      setDetail({ ...detail, modules: data.modules })
      onChanged()
    } catch (err) {
      setError(extractErrorMessage(err) || 'Speichern fehlgeschlagen.')
    } finally {
      setSavingModule(null)
    }
  }

  /**
   * Bulk-Aktion fuer "Alle Module an/aus" auf einer Rechte-Gruppe. Schickt
   * eine einzige PUT-Request mit der kompletten Module-Map, damit die
   * Detail-Ansicht nicht ueber Single-Toggles flackert.
   */
  const setAllModules = async (allowed: boolean) => {
    if (!detail) return
    setBulkSaving(true)
    setError('')
    try {
      const allIds = Object.values(grouped).flatMap((mods) => mods.map((m) => m.id))
      const modules: Record<string, boolean> = {}
      for (const id of allIds) modules[id] = allowed
      const { data } = await apiClient.put<{ modules: Record<string, boolean> }>(
        `/permission-groups/${groupId}/modules`,
        { modules },
      )
      setDetail({ ...detail, modules: data.modules })
      onChanged()
    } catch (err) {
      setError(extractErrorMessage(err) || 'Speichern fehlgeschlagen.')
    } finally {
      setBulkSaving(false)
    }
  }

  const saveMeta = async () => {
    if (!detail) return
    setSavingMeta(true)
    setError('')
    try {
      await apiClient.patch(`/permission-groups/${groupId}`, {
        name: editName.trim(),
        description: editDesc.trim() || null,
      })
      setEditingMeta(false)
      await load()
      onChanged()
    } catch (err) {
      setError(extractErrorMessage(err) || 'Speichern fehlgeschlagen.')
    } finally {
      setSavingMeta(false)
    }
  }

  if (loading || !detail) return <LoadingState />

  return (
    <div className="border border-gray-200 rounded-md bg-white">
      <div className="px-4 py-3 border-b border-gray-100">
        {editingMeta ? (
          <div className="space-y-2">
            <Input value={editName} onChange={(e) => setEditName(e.target.value)} label="Name" />
            <Textarea
              value={editDesc}
              onChange={(e) => setEditDesc(e.target.value)}
              label="Beschreibung"
              rows={2}
            />
            <div className="flex justify-end gap-2">
              <Button
                variant="secondary"
                size="sm"
                onClick={() => {
                  setEditingMeta(false)
                  setEditName(detail.name)
                  setEditDesc(detail.description ?? '')
                }}
                disabled={savingMeta}
                leadingIcon={<X className="w-3.5 h-3.5" />}
              >
                Abbrechen
              </Button>
              <Button
                size="sm"
                onClick={saveMeta}
                loading={savingMeta}
                leadingIcon={<Save className="w-3.5 h-3.5" />}
              >
                Speichern
              </Button>
            </div>
          </div>
        ) : (
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <h3 className="text-h4 text-gray-900">{detail.name}</h3>
              {detail.description && (
                <p className="text-caption text-gray-500 mt-0.5">{detail.description}</p>
              )}
            </div>
            <Button
              variant="ghost"
              size="sm"
              leadingIcon={<Pencil className="w-3.5 h-3.5" />}
              onClick={() => setEditingMeta(true)}
            >
              Bearbeiten
            </Button>
          </div>
        )}
      </div>

      {error && (
        <div className="px-4 pt-3">
          <div className="bg-red-50 border border-red-200 text-red-800 text-small rounded px-3 py-2">
            {error}
          </div>
        </div>
      )}

      <div className="px-4 py-3 space-y-3 border-b border-gray-100">
        <div className="flex items-start justify-between gap-3">
          <p className="text-caption text-gray-500">
            Welche Module bekommen Mitglieder dieser Gruppe? Persönliche Deny-Einstellungen pro
            Benutzer überschreiben diese Liste.
          </p>
          <div className="flex gap-1.5 flex-shrink-0">
            <Button variant="secondary" size="xs" disabled={bulkSaving} onClick={() => void setAllModules(true)}>
              Alle an
            </Button>
            <Button variant="secondary" size="xs" disabled={bulkSaving} onClick={() => void setAllModules(false)}>
              Alle aus
            </Button>
          </div>
        </div>
        <div className="space-y-3">
          {Object.entries(grouped).map(([category, mods]) => {
            if (!mods || mods.length === 0) return null
            return (
              <div key={category}>
                <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500 mb-1.5">
                  {category}
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                  {mods.map((mod) => {
                    const isOn = detail.modules[mod.id] === true
                    return (
                      <div
                        key={mod.id}
                        className="flex items-center justify-between gap-2 px-2.5 py-1.5 bg-white border border-gray-200 rounded"
                      >
                        <div className="min-w-0">
                          <p className="text-caption font-medium text-gray-800 truncate">
                            {mod.name}
                          </p>
                          <p className="text-caption text-gray-500 truncate">{mod.description}</p>
                        </div>
                        <Switch
                          checked={isOn}
                          disabled={savingModule === mod.id}
                          onChange={() => toggleModule(mod.id, !isOn)}
                          title={isOn ? 'In Gruppe enthalten' : 'Nicht in Gruppe'}
                        />
                      </div>
                    )
                  })}
                </div>
              </div>
            )
          })}
        </div>
      </div>

      <div className="px-4 py-3">
        <p className="text-caption font-semibold text-gray-700 mb-2">
          Mitglieder ({detail.members.length})
        </p>
        {detail.members.length === 0 ? (
          <p className="text-caption text-gray-500 italic">
            Noch keine Benutzer in dieser Gruppe. Mitgliedschaft wird in der Benutzerverwaltung
            zugewiesen.
          </p>
        ) : (
          <div className="space-y-1">
            {detail.members.map((m) => (
              <div
                key={m.userId}
                className="flex items-center justify-between px-2.5 py-1.5 bg-gray-50 border border-gray-100 rounded"
              >
                <div className="min-w-0">
                  <p className="text-small font-medium text-gray-900 truncate">{m.name}</p>
                  <p className="text-caption text-gray-500 truncate">{m.email}</p>
                </div>
                <Badge variant={m.isActive ? 'success' : 'neutral'} size="sm">
                  {m.isActive ? 'Aktiv' : 'Inaktiv'}
                </Badge>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
